class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );
    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  const FREE_SHIPPING = 6900;
  const UPSELLS = [
    { id: 55096605770076, title: 'ASKOS Olivenöl mit Zitrone 250 ml', price: '8,50 €', handle: 'askos-olivenol-aroma-mit-zitrone-250ml' },
    { id: 55096600428892, title: 'ASKOS Olivenöl mit Orange 250 ml', price: '8,50 €', handle: 'askos-olivenolaroma-mit-orange-250ml' },
    { id: 55126551232860, title: 'Kalamata & grüne Oliven 250 g', price: '3,99 €', handle: 'oliven-der-sorte-kalamata-grun-gemixt-im-vakuum-250g' },
    { id: 55126542549340, title: 'Kalamata Oliven 250 g', price: '3,99 €', handle: 'oliven-der-sorte-kalamata-im-vakuum-250g' },
    { id: 56175315583324, title: 'Gewürzmischung Griechischer Salat 50 g', price: '4,95 €', handle: 'gewurzmischung-griechischer-salat-50g' },
    { id: 56175327412572, title: 'Gewürzmischung Feta 50 g', price: '4,95 €', handle: 'gewurzmischung-feta-50g' },
    { id: 55126608642396, title: 'Oliventapenade grüne Oliven 180 g', price: '4,25 €', handle: 'oliventapenade-olivenpaste-aus-grunen-oliven-180g' },
    { id: 55126607954268, title: 'Oliventapenade grüne Oliven 80 g', price: '2,95 €', handle: 'oliventapenade-olivenpaste-aus-grunen-oliven-80g' },
    { id: 56175408054620, title: 'Gewürzmischung Gyros 50 g', price: '4,95 €', handle: 'gewurzmischung-gyros-50g' },
    { id: 56175363064156, title: 'Gewürzmischung Tzatziki 50 g', price: '4,95 €', handle: 'gewurzmischung-tzatziki-50g' },
    { id: 55126651896156, title: 'Feigenmarmelade 100 g', price: '4,50 €', handle: 'feigenmarmelade-100g' },
    { id: 55126654583132, title: 'Tomatenmarmelade 100 g', price: '4,50 €', handle: 'tomaten-marmelade-100g' },
  ];
  const productCache = new Map();
  const isGerman = () => (document.documentElement.lang || '').toLowerCase().startsWith('de');
  const money = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  const getCountry = () => String(document.querySelector('cart-drawer')?.dataset?.country || window.Shopify?.country || '').toUpperCase();

  function injectStyles() {
    if (document.getElementById('roubier-premium-cart-style')) return;
    const style = document.createElement('style');
    style.id = 'roubier-premium-cart-style';
    style.textContent = `
      cart-drawer .drawer__inner { width:min(60rem,96vw)!important; max-width:60rem!important; background:#fff!important; box-shadow:-18px 0 50px rgba(0,0,0,.16)!important; }
      cart-drawer .drawer__header { padding-bottom:1.3rem!important; }
      cart-drawer .drawer__heading { font-size:2.7rem!important; line-height:1.15!important; font-weight:700!important; margin-bottom:.5rem!important; }
      .roubier-cart-subtitle { margin:0; font-size:1.3rem; line-height:1.45; color:rgba(18,18,18,.62); }
      cart-drawer cart-drawer-items { display:block!important; flex:0 0 auto!important; min-height:25rem!important; max-height:38rem!important; overflow-y:auto!important; overflow-x:hidden!important; }
      cart-drawer .drawer__cart-items-wrapper { min-height:25rem!important; max-height:none!important; overflow:visible!important; }
      cart-drawer .cart-items { min-height:25rem!important; }
      cart-drawer .cart-item { position:relative!important; min-height:23rem!important; padding-top:2.2rem!important; padding-bottom:5.5rem!important; border-bottom:1px solid rgba(18,18,18,.09)!important; overflow:visible!important; }
      cart-drawer .cart-item__media { width:10.5rem!important; max-width:10.5rem!important; }
      cart-drawer .cart-item__image { width:9.8rem!important; height:12.5rem!important; max-width:none!important; object-fit:contain!important; border-radius:12px!important; background:#f7f5f0!important; }
      cart-drawer .cart-item__details { min-width:0!important; padding-right:1rem!important; }
      cart-drawer .cart-item__name { display:block!important; white-space:normal!important; overflow:visible!important; font-size:1.5rem!important; line-height:1.4!important; font-weight:650!important; margin-bottom:.8rem!important; }
      cart-drawer .cart-item__price-wrapper, cart-drawer .product-option { font-size:1.25rem!important; }
      cart-drawer .cart-item__totals { align-self:start!important; justify-self:end!important; white-space:nowrap!important; padding-left:1rem!important; }
      cart-drawer .cart-item__totals .price { font-size:1.45rem!important; font-weight:700!important; }
      cart-drawer .cart-item__quantity { margin-top:1.8rem!important; padding-bottom:2.5rem!important; overflow:visible!important; }
      cart-drawer .cart-item__quantity-wrapper { display:flex!important; align-items:center!important; gap:1rem!important; flex-wrap:nowrap!important; min-height:5rem!important; overflow:visible!important; }
      cart-drawer quantity-input.quantity { width:12.5rem!important; min-width:12.5rem!important; height:4.5rem!important; border-radius:999px!important; overflow:hidden!important; }
      cart-drawer .quantity__button { width:4rem!important; min-width:4rem!important; height:4.3rem!important; }
      cart-drawer .quantity__input { min-width:4rem!important; font-size:1.4rem!important; }
      cart-drawer cart-remove-button { display:flex!important; align-items:center!important; justify-content:center!important; flex-shrink:0!important; margin-left:.5rem!important; }
      cart-drawer cart-remove-button .button { width:4.3rem!important; min-width:4.3rem!important; height:4.3rem!important; padding:0!important; }
      cart-drawer .drawer__footer { border-top:1px solid rgba(18,18,18,.08)!important; }
      #CartDrawer details[id^="Details-"] { display:none!important; }
      cart-drawer .totals__total { font-weight:700!important; }
      cart-drawer .totals__total-value { font-size:1.8rem!important; font-weight:700!important; }
      cart-drawer #CartDrawer-Checkout { min-height:5.4rem!important; border-radius:12px!important; font-weight:700!important; font-size:1.55rem!important; }
      .roubier-shipping { margin:0 0 1.8rem; padding:1.5rem; border-radius:14px; background:#f8f6f1; }
      .roubier-shipping__text { margin:0 0 1rem; font-size:1.35rem; line-height:1.4; }
      .roubier-shipping__bar { position:relative!important; width:100%!important; height:8px!important; border-radius:999px!important; overflow:hidden!important; background:rgba(18,18,18,.12)!important; }
      .roubier-shipping__fill { display:block!important; position:absolute!important; top:0!important; left:0!important; height:8px!important; width:0; min-width:0!important; max-width:none!important; border-radius:999px!important; background:rgb(182,146,75)!important; transform:none!important; opacity:1!important; }
      .roubier-shipping__scale { display:flex; justify-content:space-between; margin-top:.6rem; font-size:1.1rem; color:rgba(18,18,18,.55); }
      .roubier-upsells { margin:2rem 0 1.8rem; }
      .roubier-upsells__title { margin:0 0 1rem; font-size:1.55rem; font-weight:700; }
      .roubier-upsell { display:grid; grid-template-columns:64px minmax(0,1fr) auto; gap:1rem; align-items:center; padding:.9rem 0; border-bottom:1px solid rgba(18,18,18,.07); }
      .roubier-upsell__image { width:64px; height:64px; object-fit:contain; border-radius:10px; background:#f7f5f0; }
      .roubier-upsell__name { display:block; font-size:1.25rem; line-height:1.3; font-weight:600; }
      .roubier-upsell__price { display:block; margin-top:.3rem; font-size:1.2rem; color:rgba(18,18,18,.65); }
      .roubier-upsell__add { width:4.3rem; min-width:4.3rem; height:4.3rem; border:1px solid rgb(182,146,75); border-radius:999px; background:#fff; color:#111; font-size:2rem; line-height:1; cursor:pointer; }
      .roubier-upsell__add:hover { background:#111; color:#fff; border-color:#111; }
      .roubier-upsell__add[disabled] { opacity:.5; cursor:wait; }
      .roubier-trust { margin:1.3rem 0 0; text-align:center; font-size:1.15rem; color:rgba(18,18,18,.58); }
      .roubier-cart-tab { position:fixed; right:0; top:50%; transform:translateY(-50%); z-index:38; width:50px; min-height:78px; border:0; border-radius:14px 0 0 14px; background:#111; color:#fff; cursor:pointer; box-shadow:0 4px 18px rgba(0,0,0,.18); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.45rem; }
      .roubier-cart-tab svg { width:22px; height:22px; }
      .roubier-cart-tab__count { min-width:20px; height:20px; padding:0 5px; display:flex; align-items:center; justify-content:center; border-radius:999px; background:rgb(182,146,75); color:#fff; font-size:1.05rem; font-weight:700; }
      body.roubier-cart-open .roubier-cart-tab { opacity:0; visibility:hidden; pointer-events:none; }
      .roubier-cart-float { display:none!important; }
      @media screen and (max-width:749px) {
        cart-drawer .drawer__inner { width:100vw!important; max-width:100vw!important; }
        cart-drawer cart-drawer-items { min-height:22rem!important; max-height:34rem!important; }
        cart-drawer .drawer__cart-items-wrapper, cart-drawer .cart-items { min-height:22rem!important; }
        cart-drawer .cart-item { min-height:21rem!important; padding-top:1.5rem!important; padding-bottom:5rem!important; }
        cart-drawer .drawer__heading { font-size:2.25rem!important; }
        cart-drawer .cart-item__media { width:8.5rem!important; max-width:8.5rem!important; }
        cart-drawer .cart-item__image { width:8rem!important; height:10rem!important; }
        cart-drawer .cart-item__name { font-size:1.35rem!important; }
        cart-drawer quantity-input.quantity { width:11.5rem!important; min-width:11.5rem!important; height:4.2rem!important; }
        cart-drawer .quantity__button { width:3.6rem!important; min-width:3.6rem!important; }
        .roubier-cart-tab { width:44px; min-height:68px; }
        .roubier-upsell { grid-template-columns:58px minmax(0,1fr) auto; }
        .roubier-upsell__image { width:58px; height:58px; }
      }
    `;
    document.head.appendChild(style);
  }

  async function getCart() {
    const route = window.routes?.cart_url || '/cart';
    const response = await fetch(`${route}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Warenkorb konnte nicht geladen werden.');
    return response.json();
  }

  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const response = await fetch(`/products/${handle}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const product = await response.json();
      productCache.set(handle, product);
      return product;
    } catch (_) {
      return null;
    }
  }

  function enhanceHeader() {
    const drawer = document.querySelector('cart-drawer');
    const heading = drawer?.querySelector('.drawer__heading');
    if (!heading) return;
    heading.textContent = isGerman() ? 'Dein Warenkorb' : 'Your cart';
    let subtitle = drawer.querySelector('.roubier-cart-subtitle');
    if (!subtitle) {
      subtitle = document.createElement('p');
      subtitle.className = 'roubier-cart-subtitle';
      heading.insertAdjacentElement('afterend', subtitle);
    }
    subtitle.textContent = isGerman()
      ? 'Deine Auswahl von Kos – fast bereit für den Versand.'
      : 'Your selection from Kos – almost ready to ship.';
  }

  function renderShipping(cart) {
    const drawer = document.querySelector('cart-drawer');
    const footer = drawer?.querySelector('.drawer__footer');
    if (!footer) return;
    footer.querySelector('.roubier-shipping')?.remove();
    if (getCountry() !== 'DE') return;

    const subtotal = Number(cart.items_subtotal_price || cart.total_price || 0);
    const remaining = Math.max(FREE_SHIPPING - subtotal, 0);
    const percent = Math.max(0, Math.min((subtotal / FREE_SHIPPING) * 100, 100));
    const reached = subtotal >= FREE_SHIPPING;

    const block = document.createElement('div');
    block.className = 'roubier-shipping';
    block.innerHTML = `
      <p class="roubier-shipping__text">${
        reached
          ? 'Kostenloser Versand erreicht ✓'
          : `Nur noch <strong>${money(remaining)}</strong> bis zum kostenlosen Versand!`
      }</p>
      <div class="roubier-shipping__bar" role="progressbar" aria-valuemin="0" aria-valuemax="69" aria-valuenow="${Math.min(subtotal / 100, 69).toFixed(2)}">
        <div class="roubier-shipping__fill"></div>
      </div>
      <div class="roubier-shipping__scale"><span>0 €</span><span>69 €</span></div>
    `;
    footer.prepend(block);

    const fill = block.querySelector('.roubier-shipping__fill');
    if (fill) {
      fill.style.cssText = `display:block!important;position:absolute!important;top:0!important;left:0!important;height:8px!important;width:${percent}%!important;min-width:0!important;max-width:none!important;border-radius:999px!important;background:rgb(182,146,75)!important;transform:none!important;opacity:1!important;`;
    }
  }

  async function renderUpsells(cart) {
    const drawer = document.querySelector('cart-drawer');
    const footer = drawer?.querySelector('.drawer__footer');
    if (!footer) return;
    footer.querySelector('.roubier-upsells')?.remove();
    const inCart = new Set(cart.items.map((item) => Number(item.variant_id)));
    const products = UPSELLS.filter((item) => !inCart.has(item.id)).slice(0, 3);
    if (!products.length) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'roubier-upsells';
    wrapper.innerHTML = `<p class="roubier-upsells__title">${isGerman() ? 'Passt perfekt dazu' : 'Perfect with your order'}</p><div class="roubier-upsells__list"></div>`;
    const list = wrapper.querySelector('.roubier-upsells__list');

    products.forEach((product) => {
      const item = document.createElement('div');
      item.className = 'roubier-upsell';
      item.innerHTML = `
        <img class="roubier-upsell__image" alt="" width="64" height="64" loading="lazy">
        <div><span class="roubier-upsell__name">${product.title}</span><span class="roubier-upsell__price">${product.price}</span></div>
        <button type="button" class="roubier-upsell__add" data-variant-id="${product.id}" aria-label="${product.title} hinzufügen" title="Hinzufügen">+</button>
      `;
      list.appendChild(item);
      const image = item.querySelector('.roubier-upsell__image');
      getProduct(product.handle).then((data) => {
        if (!data || !image) return;
        const src = data.featured_image || data.images?.[0] || '';
        if (src) { image.src = src; image.alt = product.title; }
      });
      item.querySelector('.roubier-upsell__add').addEventListener('click', async (event) => {
        const button = event.currentTarget;
        button.disabled = true;
        button.textContent = '…';
        try {
          await addUpsell(product.id);
        } catch (error) {
          console.error(error);
          button.disabled = false;
          button.textContent = '+';
        }
      });
    });

    const totals = footer.querySelector('.totals') || footer.querySelector('.cart__ctas');
    if (totals) totals.insertAdjacentElement('beforebegin', wrapper);
    else footer.appendChild(wrapper);
  }

  function renderTrust() {
    const footer = document.querySelector('cart-drawer')?.querySelector('.drawer__footer');
    if (!footer) return;
    footer.querySelector('.roubier-trust')?.remove();
    const trust = document.createElement('p');
    trust.className = 'roubier-trust';
    trust.textContent = isGerman() ? '🔒 Sicher bezahlen · SSL-verschlüsselt' : '🔒 Secure payment · SSL encrypted';
    footer.appendChild(trust);
  }

  function renderTab(cart) {
    let tab = document.querySelector('.roubier-cart-tab');
    if (!cart.item_count) { tab?.remove(); return; }
    if (!tab) {
      tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'roubier-cart-tab';
      tab.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 3h2l2.4 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L21 7H7"/><circle cx="10" cy="20" r="1"/><circle cx="18" cy="20" r="1"/></svg><span class="roubier-cart-tab__count"></span>`;
      tab.setAttribute('aria-label', isGerman() ? 'Warenkorb öffnen' : 'Open cart');
      tab.addEventListener('click', () => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(tab);
      });
      document.body.appendChild(tab);
    }
    const count = tab.querySelector('.roubier-cart-tab__count');
    if (count) count.textContent = cart.item_count;
  }

  async function addUpsell(variantId) {
    const drawer = document.querySelector('cart-drawer');
    const addUrl = window.routes?.cart_add_url || '/cart/add';
    const response = await fetch(`${addUrl}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error('Produkt konnte nicht hinzugefügt werden.');
    const state = await response.json();
    if (drawer && state.sections) drawer.renderContents({ ...state, id: Number(variantId) });
    setTimeout(refresh, 150);
  }

  async function refresh() {
    try {
      injectStyles();
      const cart = await getCart();
      enhanceHeader();
      renderShipping(cart);
      await renderUpsells(cart);
      renderTrust();
      renderTab(cart);
    } catch (error) {
      console.error('Roubier Premium Cart:', error);
    }
  }

  const originalOpen = CartDrawer.prototype.open;
  CartDrawer.prototype.open = function (...args) {
    const result = originalOpen.apply(this, args);
    document.body.classList.add('roubier-cart-open');
    setTimeout(refresh, 80);
    return result;
  };

  const originalClose = CartDrawer.prototype.close;
  CartDrawer.prototype.close = function (...args) {
    const result = originalClose.apply(this, args);
    document.body.classList.remove('roubier-cart-open');
    setTimeout(refresh, 80);
    return result;
  };

  const originalRenderContents = CartDrawer.prototype.renderContents;
  CartDrawer.prototype.renderContents = function (...args) {
    const result = originalRenderContents.apply(this, args);
    setTimeout(refresh, 150);
    return result;
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(refresh, 120));
  }

  window.RoubierPremiumCart = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
  window.addEventListener('pageshow', refresh);
})();